import { useEffect, useState } from 'react';

import {
  generateShareImage,
  shareOrDownloadImage,
  type ImageGenParams,
} from '~/client/lib/imageGen';
import { copyToClipboard, emailLink, nativeShare, smsLink, whatsappLink } from '~/client/lib/share';

import { Icon } from './Icons';
import { Button } from './Primitives';

interface ShareSheetProps {
  url: string;
  title: string;
  editLink?: string;
  imageParams?: ImageGenParams | null;
  onClose: () => void;
}

export function ShareSheet({ url, title, editLink, imageParams, onClose }: ShareSheetProps) {
  const [copied, setCopied] = useState(false);
  const [editCopied, setEditCopied] = useState(false);
  const [imageBusy, setImageBusy] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [imageDone, setImageDone] = useState<'shared' | 'downloaded' | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copy = async (text: string, kind: 'main' | 'edit') => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    if (kind === 'main') {
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } else {
      setEditCopied(true);
      setTimeout(() => setEditCopied(false), 1400);
    }
  };

  const handleNative = async () => {
    const result = await nativeShare(url, title, `${title}: ${url}`);
    if (result === 'shared') onClose();
  };

  const handleImage = async () => {
    if (!imageParams || imageBusy) return;
    setImageBusy(true);
    setImageError(null);
    setImageDone(null);
    try {
      const blob = await generateShareImage(imageParams);
      const filename = `whomfree-${
        imageParams.pollTitle
          .replace(/[^a-z0-9]+/gi, '-')
          .toLowerCase()
          .slice(0, 40) || 'poll'
      }.png`;
      const outcome = await shareOrDownloadImage(blob, filename);
      if (outcome === 'shared') setImageDone('shared');
      else if (outcome === 'downloaded') setImageDone('downloaded');
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'failed to make image');
    } finally {
      setImageBusy(false);
      setTimeout(() => setImageDone(null), 2000);
    }
  };

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="row between" style={{ marginBottom: 16 }}>
          <h2
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              margin: 0,
            }}
          >
            share the link
          </h2>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
            aria-label="close"
          >
            <Icon name="x" size={14} />
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0, marginBottom: 16, fontSize: 14 }}>
          anyone with the link can drop their dates. no sign-up.
        </p>

        <div className="row" style={{ gap: 8, marginBottom: 16 }}>
          <div
            className="input mono"
            style={{
              flex: 1,
              fontSize: 14,
              background: 'var(--cream)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {url}
          </div>
          <Button variant={copied ? 'ink' : 'primary'} onClick={() => void copy(url, 'main')}>
            {copied ? (
              <>
                <Icon name="check" size={14} /> copied
              </>
            ) : (
              <>
                <Icon name="copy" size={14} /> copy
              </>
            )}
          </Button>
        </div>

        <div className="field-label">or send via</div>
        <div className="row row-wrap" style={{ gap: 8, marginTop: 8 }}>
          {canNativeShare && (
            <Button variant="ghost" onClick={() => void handleNative()}>
              <Icon name="share" size={14} /> share…
            </Button>
          )}
          <a
            className="btn btn-ghost"
            href={whatsappLink(url, title)}
            target="_blank"
            rel="noreferrer"
          >
            WhatsApp
          </a>
          <a className="btn btn-ghost" href={smsLink(url, title)}>
            iMessage
          </a>
          <a className="btn btn-ghost" href={emailLink(url, title)}>
            Email
          </a>
        </div>

        {imageParams && (
          <>
            <div className="field-label" style={{ marginTop: 20 }}>
              or share a poster
            </div>
            <div className="row row-wrap" style={{ gap: 8, marginTop: 8, alignItems: 'center' }}>
              <Button
                variant={imageDone ? 'ink' : 'primary'}
                onClick={() => void handleImage()}
                disabled={imageBusy}
              >
                {imageBusy ? (
                  <>making…</>
                ) : imageDone === 'shared' ? (
                  <>
                    <Icon name="check" size={14} /> shared
                  </>
                ) : imageDone === 'downloaded' ? (
                  <>
                    <Icon name="check" size={14} /> saved
                  </>
                ) : (
                  <>
                    <Icon name="sparkle" size={14} /> share image
                  </>
                )}
              </Button>
              <span className="muted" style={{ fontSize: 12 }}>
                a small picture of the result + dates.
              </span>
            </div>
            {imageError && (
              <div
                role="alert"
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  background: 'var(--coral-soft)',
                  color: 'var(--coral-deep)',
                  borderRadius: 8,
                  fontSize: 13,
                }}
              >
                {imageError}
              </div>
            )}
          </>
        )}

        {editLink && (
          <>
            <div className="field-label" style={{ marginTop: 20 }}>
              your edit link (keep it safe)
            </div>
            <div className="row" style={{ gap: 8, marginTop: 4 }}>
              <div
                className="input mono"
                style={{
                  flex: 1,
                  fontSize: 12,
                  background: 'var(--cream)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {editLink}
              </div>
              <Button
                variant={editCopied ? 'ink' : 'ghost'}
                onClick={() => void copy(editLink, 'edit')}
              >
                {editCopied ? (
                  <>
                    <Icon name="check" size={14} /> copied
                  </>
                ) : (
                  <>
                    <Icon name="copy" size={14} /> copy
                  </>
                )}
              </Button>
            </div>
            <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
              this lets you edit your dates on a different device.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
